<template>
  <v-row>
    <v-breadcrumbs :items="['reports', 'acquisitions']" divider="/"></v-breadcrumbs>
  </v-row>

  <!-- input card -->
  <v-row>
    <v-col cols="12" md="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">acquisitions report</v-card-title>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text>
          <v-container>
            <v-row align="center">
              <v-col>
                <v-select
                  v-model="selectedBrokerAccount"
                  :items="brokerAccounts"
                  item-title="label"
                  item-value="id"
                  label="broker account"
                  density="compact"
                  variant="outlined"
                  hide-details
                ></v-select>
              </v-col>
              <v-col>
                <v-select
                  v-model="selectedFY"
                  :items="financialYears"
                  label="financial year"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  @update:modelValue="onFYSelect"
                ></v-select>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="startDate"
                  type="date"
                  label="start date"
                  density="compact"
                  variant="outlined"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="endDate"
                  type="date"
                  label="end date"
                  density="compact"
                  variant="outlined"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="auto">
                <v-btn @click="getAcquisitions" :loading="loading" :disabled="!selectedBrokerAccount || !startDate || !endDate">go</v-btn>
                <v-btn class="ml-2" variant="outlined" @click="exportPDF" :disabled="!report" prepend-icon="mdi-file-pdf-box">pdf</v-btn>
                <v-btn class="ml-2" variant="outlined" @click="exportCSV" :disabled="!report" prepend-icon="mdi-file-delimited">csv</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- summary cards -->
  <v-row v-if="report">
    <v-col cols="12" md="3">
      <v-card :elevation="1">
        <v-card-text class="text-left">
          <div class="text-grey text-caption">total net amount</div>
          <div class="text-h6">{{ formatCurrency(report.summary.grand_total.total_net_amount) }}</div>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col cols="12" md="3">
      <v-card :elevation="1">
        <v-card-text class="text-left">
          <div class="text-grey text-caption">total brokerage</div>
          <div class="text-h6">{{ formatCurrency(report.summary.grand_total.total_brokerage) }}</div>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col cols="12" md="3">
      <v-card :elevation="1">
        <v-card-text class="text-left">
          <div class="text-grey text-caption">transactions</div>
          <div class="text-h6">{{ report.summary.grand_total.transaction_count }}</div>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col cols="12" md="3">
      <v-card :elevation="1">
        <v-card-text class="text-left">
          <div class="text-grey text-caption">tickers</div>
          <div class="text-h6">{{ report.summary.grand_total.ticker_count }}</div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- type subtotals -->
  <v-row v-if="report && Object.keys(report.summary.by_type).length > 1">
    <v-col cols="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">totals by type</v-card-title>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text>
          <v-container class="table-scroll">
            <table>
              <tr>
                <th class="text-left">type</th>
                <th class="text-right">count</th>
                <th class="text-right">brokerage</th>
                <th class="text-right">net amount</th>
              </tr>
              <tr v-for="(subtotal, type) in report.summary.by_type" :key="type">
                <td class="text-left">{{ type }}</td>
                <td class="text-right">{{ subtotal.transaction_count }}</td>
                <td class="text-right">{{ formatCurrency(subtotal.total_brokerage) }}</td>
                <td class="text-right">{{ formatCurrency(subtotal.total_net_amount) }}</td>
              </tr>
              <tr class="font-weight-bold">
                <td class="text-left">total</td>
                <td class="text-right">{{ report.summary.grand_total.transaction_count }}</td>
                <td class="text-right">{{ formatCurrency(report.summary.grand_total.total_brokerage) }}</td>
                <td class="text-right">{{ formatCurrency(report.summary.grand_total.total_net_amount) }}</td>
              </tr>
            </table>
          </v-container>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- transactions table -->
  <v-row v-if="report && report.acquisitions.length > 0">
    <v-col cols="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">transactions</v-card-title>
          <template v-slot:append>
            <v-btn v-if="report.has_foreign_currency" icon="mdi-currency-usd" :variant="isBaseCurrency ? 'tonal' : 'text'" @click="isBaseCurrency = !isBaseCurrency"></v-btn>
          </template>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text>
          <v-container class="table-scroll">
            <table>
              <tr>
                <th class="text-left">date</th>
                <th class="text-left">ticker</th>
                <th class="text-left">type</th>
                <th class="text-right">qty</th>
                <th class="text-right">price</th>
                <th class="text-right">brokerage</th>
                <th class="text-right">net amount</th>
              </tr>
              <tr v-for="txn in report.acquisitions" :key="txn.id">
                <td class="text-left">{{ showDateFormat(txn.txn_date) }}</td>
                <td class="text-left">{{ txn.ticker }}</td>
                <td class="text-left">{{ txn.txn_type }}</td>
                <td class="text-right">{{ txn.qty.toLocaleString() }}</td>
                <td class="text-right">{{ isBaseCurrency && txn.base_currency_net_amount ? formatCurrency(txn.base_currency_net_amount / txn.qty) : formatCurrency(txn.price) }}</td>
                <td class="text-right">{{ isBaseCurrency && txn.base_currency_spot_rate ? formatCurrency(txn.brokerage / txn.base_currency_spot_rate) : formatCurrency(txn.brokerage) }}</td>
                <td class="text-right">{{ isBaseCurrency && txn.base_currency_net_amount ? formatCurrency(txn.base_currency_net_amount) : formatCurrency(txn.net_amount) }}</td>
              </tr>
              <tr class="font-weight-bold">
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td class="text-right">{{ formatCurrency(report.summary.grand_total.total_brokerage) }}</td>
                <td class="text-right">{{ formatCurrency(report.summary.grand_total.total_net_amount) }}</td>
              </tr>
            </table>
          </v-container>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- empty state -->
  <v-row v-if="report && report.acquisitions.length === 0">
    <v-col cols="12">
      <v-card :elevation="1">
        <v-card-text class="text-center text-grey pa-8">
          no transactions found in this date range
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from 'axios';
import { mapState, mapMutations } from 'vuex';
import moment from 'moment';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import SpaceGroteskRegular from '@/assets/SpaceGroteskFont.js';

export default {
  data() {
    return {
      brokerAccounts: [],
      selectedBrokerAccount: null,
      selectedFY: null,
      startDate: '',
      endDate: '',
      report: null,
      loading: false,
      isBaseCurrency: true
    };
  },
  async created() {
    this.getBrokerAccounts();
  },
  computed: {
    ...mapState({ token: "token" }),
    financialYears() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const latestEndYear = currentMonth >= 6 ? currentYear + 1 : currentYear;
      const years = [];
      for (let endYear = latestEndYear; endYear >= latestEndYear - 5; endYear--) {
        years.push('FY' + (endYear - 1).toString().slice(2) + '/' + endYear.toString().slice(2));
      }
      return years;
    }
  },
  methods: {
    ...mapMutations(["showSnackbar"]),
    onFYSelect(val) {
      if (!val) return;
      const parts = val.replace('FY', '').split('/');
      const startYear = 2000 + parseInt(parts[0]);
      const endYear = 2000 + parseInt(parts[1]);
      this.startDate = startYear + '-07-01';
      this.endDate = endYear + '-06-30';
      if (this.selectedBrokerAccount) this.getAcquisitions();
    },
    async getBrokerAccounts() {
      try {
        const response = await axios.get("/securities/brokeraccounts/", {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        this.brokerAccounts = response.data.map(a => ({
          id: a.id,
          label: a.institution + ' - ' + a.account_name
        }));
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load broker accounts' });
      }
    },
    async getAcquisitions() {
      this.loading = true;
      this.report = null;
      this.isBaseCurrency = true;
      try {
        const response = await axios.get(
          "/reports/acquisitions/" + this.selectedBrokerAccount + "?start_date=" + this.startDate + "&end_date=" + this.endDate,
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.report = response.data;
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load acquisitions report' });
      }
      this.loading = false;
    },
    showDateFormat(rawDate) {
      return moment(rawDate).format('DD/MM/YYYY');
    },
    formatCurrency(val) {
      if (val == null) return '-';
      return val.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    selectedAccountLabel() {
      const acct = this.brokerAccounts.find(a => a.id === this.selectedBrokerAccount);
      return acct ? acct.label : '';
    },
    async exportPDF() {
      const doc = new jsPDF();

      doc.addFileToVFS('SpaceGrotesk-Regular.ttf', SpaceGroteskRegular);
      doc.addFont('SpaceGrotesk-Regular.ttf', 'SpaceGrotesk', 'normal');
      doc.setFont('SpaceGrotesk');

      const label = this.selectedAccountLabel();

      try {
        const response = await fetch('/w_icon_medium.png');
        const blob = await response.blob();
        const dataUrl = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        doc.addImage(dataUrl, 'PNG', 14, 10, 12, 12);
      } catch (e) {
        // continue without logo
      }

      doc.setFontSize(18);
      doc.text('wellfi', 29, 19);

      doc.setFontSize(12);
      doc.text('Acquisitions Report', 14, 30);
      doc.setFontSize(10);
      doc.text(label + '  |  ' + moment(this.startDate).format('DD-MM-YYYY') + ' to ' + moment(this.endDate).format('DD-MM-YYYY'), 14, 36);

      // summary
      doc.setFontSize(9);
      doc.text('Net Amount: ' + this.formatCurrency(this.report.summary.grand_total.total_net_amount) +
        '  |  Brokerage: ' + this.formatCurrency(this.report.summary.grand_total.total_brokerage) +
        '  |  Transactions: ' + this.report.summary.grand_total.transaction_count +
        '  |  Tickers: ' + this.report.summary.grand_total.ticker_count, 14, 42);

      // type subtotals table
      const byType = this.report.summary.by_type;
      const typeKeys = Object.keys(byType);
      if (typeKeys.length > 1) {
        doc.setFontSize(11);
        doc.text('Totals by Type', 14, 52);

        const typeRows = typeKeys.map(type => [
          type,
          byType[type].transaction_count,
          this.formatCurrency(byType[type].total_brokerage),
          this.formatCurrency(byType[type].total_net_amount)
        ]);
        typeRows.push([
          'Total',
          this.report.summary.grand_total.transaction_count,
          this.formatCurrency(this.report.summary.grand_total.total_brokerage),
          this.formatCurrency(this.report.summary.grand_total.total_net_amount)
        ]);

        autoTable(doc, {
          startY: 58,
          head: [['Type', 'Count', 'Brokerage', 'Net Amount']],
          body: typeRows,
          styles: { fontSize: 8, font: 'SpaceGrotesk' },
          headStyles: { fillColor: [0, 0, 0], font: 'SpaceGrotesk' },
          didParseCell: (data) => {
            if (data.section === 'body' && data.row.index === typeRows.length - 1) {
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });
      }

      // transactions table
      let txnStartY = typeKeys.length > 1 ? doc.lastAutoTable.finalY + 10 : 52;
      doc.setFontSize(11);
      doc.text('Transactions', 14, txnStartY);

      const headers = ['Date', 'Ticker', 'Type', 'Qty', 'Price', 'Brokerage', 'Net Amount'];
      const rows = this.report.acquisitions.map(txn => [
        this.showDateFormat(txn.txn_date),
        txn.ticker,
        txn.txn_type,
        txn.qty.toLocaleString(),
        this.formatCurrency(txn.price),
        this.formatCurrency(txn.brokerage),
        this.formatCurrency(txn.net_amount)
      ]);
      rows.push([
        '', '', '', '', '',
        this.formatCurrency(this.report.summary.grand_total.total_brokerage),
        this.formatCurrency(this.report.summary.grand_total.total_net_amount)
      ]);

      autoTable(doc, {
        startY: txnStartY + 6,
        head: [headers],
        body: rows,
        styles: { fontSize: 8, font: 'SpaceGrotesk' },
        headStyles: { fillColor: [0, 0, 0], font: 'SpaceGrotesk' },
        didParseCell: (data) => {
          if (data.section === 'body' && data.row.index === rows.length - 1) {
            data.cell.styles.fontStyle = 'bold';
          }
        }
      });

      doc.save('acquisitions_' + this.startDate + '_' + this.endDate + '.pdf');
    },
    exportCSV() {
      const label = this.selectedAccountLabel();
      const lines = [];

      lines.push('Acquisitions Report - ' + label + ' - ' + moment(this.startDate).format('DD-MM-YYYY') + ' to ' + moment(this.endDate).format('DD-MM-YYYY'));
      lines.push('');
      lines.push('Summary');
      lines.push('Total Net Amount,' + this.report.summary.grand_total.total_net_amount.toFixed(2));
      lines.push('Total Brokerage,' + this.report.summary.grand_total.total_brokerage.toFixed(2));
      lines.push('Transactions,' + this.report.summary.grand_total.transaction_count);
      lines.push('Tickers,' + this.report.summary.grand_total.ticker_count);

      // type subtotals
      const byType = this.report.summary.by_type;
      const typeKeys = Object.keys(byType);
      if (typeKeys.length > 1) {
        lines.push('');
        lines.push('Totals by Type');
        lines.push(['Type', 'Count', 'Brokerage', 'Net Amount'].join(','));
        for (const type of typeKeys) {
          lines.push([type, byType[type].transaction_count, byType[type].total_brokerage.toFixed(2), byType[type].total_net_amount.toFixed(2)].join(','));
        }
        lines.push(['Total', this.report.summary.grand_total.transaction_count, this.report.summary.grand_total.total_brokerage.toFixed(2), this.report.summary.grand_total.total_net_amount.toFixed(2)].join(','));
      }

      // transactions
      lines.push('');
      lines.push('Transactions');
      lines.push(['Date', 'Ticker', 'Type', 'Qty', 'Price', 'Brokerage', 'Net Amount'].join(','));
      this.report.acquisitions.forEach(txn => {
        lines.push([
          this.showDateFormat(txn.txn_date),
          txn.ticker,
          txn.txn_type,
          txn.qty,
          txn.price != null ? txn.price.toFixed(2) : '',
          txn.brokerage != null ? txn.brokerage.toFixed(2) : '',
          txn.net_amount != null ? txn.net_amount.toFixed(2) : ''
        ].join(','));
      });
      lines.push(['', '', '', '', '', this.report.summary.grand_total.total_brokerage.toFixed(2), this.report.summary.grand_total.total_net_amount.toFixed(2)].join(','));

      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'acquisitions_' + this.startDate + '_' + this.endDate + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
* {
  font-family: "Space Grotesk", "Courier New", Courier, monospace, sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

:deep(*) {
  font-family: "Space Grotesk", "Courier New", Courier, monospace, sans-serif !important;
}

table {
  width: 100%;
  border-collapse: collapse;
}
</style>
